import React, { useState, useEffect, useRef } from 'react';
import { BillingComponent } from './invoice';

import './App.css';
import { api } from './api';

function App() {
  const billingRef = useRef();

  const [view, setView] = useState('inventory');
  const [message, setMessage] = useState("");
  const [lang, setLang] = useState('ur');
  const [searchTerm, setSearchTerm] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [inventorySearchInput, setInventorySearchInput] = useState("");
  const [inventorySearchTerm, setInventorySearchTerm] = useState("");
  const [receivedAmount, setReceivedAmount] = useState(0);
  const [cart, setCart] = useState([]);

  // --- ORDER MANAGEMENT STATE ---
  const [orders, setOrders] = useState([]);
  const [editingOrderId, setEditingOrderId] = useState(null);

  const formatQty = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n.toFixed(2) : '0.00';
  };

  // --- 1. STATE INITIALIZATION ---
  const [inventory, setInventory] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);

  // --- 2. DATA FETCHING ---
  const fetchData = async () => {
    const prods = await api.getProducts();
    const custs = await api.getCustomers();
    const ords = await api.getOrders();

    if (Array.isArray(prods)) setInventory(prods);
    if (Array.isArray(custs)) setCustomers(custs);
    if (Array.isArray(ords)) setOrders(ords);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // --- 3. LOGIC & TRANSLATIONS ---
  const t = {
    en: {
      storeName: "Azeem Karyana Store",
      inventory: "Inventory",
      billing: "Billing",
      customers: "Customers",
      orders: "Orders",
      addItem: "Add New Item",
      addCustomer: "Add New Customer",
      name: "Name",
      phone: "Phone Number",
      address: "Address",
      buyPrice: "Buy Price",
      sellPrice: "Sell Price",
      stock: "Stock Quantity",
      unit: "Unit",
      save: "Save",
      remaining: "Remaining",
      selectItems: "Select Items",
      total: "Total Amount",
      print: "Print Bill",
      newBill: "New Bill",
      checkout: "Checkout & Save",
      updateOrder: "Update Order",
      thanks: "Thank you!",
      kg: "kg",
      piece: "Unit",
      Ariwala: "Adda Kameer",
      date: "Date",
      time: "Time",
      item: "Item",
      qty: "Qty",
      bags: "Bags",
      outOfStock: "Out of Stock!",
      bagWeight: "Weight per Bag (kg)",
      bagPrice: "Price per Bag",
      search: "Search...",
      deleteAction: "Delete",
      editAction: "Edit",
      confirmDelete: "Are you sure?",
      received: "Received",
      change: "Change",
      selectWeight: "Select Weight",
      devBy: "Developed Software By DotTechHub ",
      customerSelect: "Select Customer (Optional)",
      orderId: "Order ID",
      customer: "Customer",
      actions: "Actions",
      profit: "Profit",
      totalOrders: "Total Orders",
      totalRevenue: "Total Revenue",
      profitLoss: "Profit/Loss"
    },
    ur: {
      storeName: "عظیم کریانہ سٹور",
      inventory: "سٹاک مینیجر",
      billing: "بلنگ (POS)",
      customers: "گاہک کی تفصیلات",
      orders: "آرڈرز",
      addItem: "نئی آئٹم شامل کریں",
      addCustomer: "نیا گاہک شامل کریں",
      name: "نام",
      phone: "فون نمبر",
      address: "پتہ",
      buyPrice: "خرید قیمت",
      sellPrice: "قیمت", // Shortened from فروخت قیمت
      stock: "کل مقدار",
      unit: "یونٹ",
      save: "محفوظ کریں",
      remaining: "باقی سٹاک",
      selectItems: "آئٹم منتخب کریں",
      total: "ٹوٹل", // Shortened/Changed to common usage
      print: "پرنٹ بل",
      newBill: "نیا بل",
      checkout: "چیک آؤٹ اور محفوظ کریں",
      updateOrder: "آرڈر اپ ڈیٹ کریں",
      thanks: "تشریف لانے کا شکریہ!",
      kg: "کلو",
      piece: "عدد",
      Ariwala: "اڈا کمیر",
      date: "تاریخ",
      time: "وقت",
      item: "آئیٹم",
      qty: "تعداد", // Shortened from تعداد/وزن
      bags: "تھری", // Shortened from پیکٹ/تھری
      outOfStock: "سٹاک ختم ہے!",
      bagWeight: "ایک بیگ کا وزن",
      bagPrice: "ایک بیگ کی قیمت",
      search: "تلاش کریں...",
      deleteAction: "حذف کریں",
      editAction: "ترمیم کریں",
      confirmDelete: "کیا آپ واقعی حذف کرنا چاہتے ہیں؟",
      received: "وصول",
      change: "باقی",
      selectWeight: "وزن منتخب کریں",
      devBy: "تیار کردہ: ڈاٹ ٹیک ہب",
      customerSelect: "گاہک منتخب کریں (اختیاری)",
      orderId: "آرڈر نمبر",
      customer: "گاہک",
      actions: "کارروائی",
      profit: "منافع",
      totalOrders: "کل آرڈرز",
      totalRevenue: "کل آمدنی",
      profitLoss: "منافع / نقصان"
    }
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const newCustomer = {
      name: formData.get('cName'),
      phone: formData.get('cPhone'),
      address: formData.get('cAddress')
    };
    const res = await api.addCustomer(newCustomer);
    if (!res.error) {
      alert(res.message);
      fetchData(); // Refresh list
      e.target.reset();
    } else {
      alert("Error: " + res.message);
    }
  };

  const deleteCustomer = async (id) => {
    if (window.confirm(t[lang].confirmDelete)) {
      await api.deleteCustomer(id);
      fetchData();
    }
  };

  const filteredCustomers = customers.filter(c =>
    c.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.phone.includes(customerSearch)
  );

  const handleAddProduct = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const newProduct = {
      name: formData.get('pName'),
      buyPrice: parseFloat(formData.get('pBuyPrice')),
      sellPrice: parseFloat(formData.get('pSellPrice')),
      totalStock: parseFloat(formData.get('pStock')),
      unit: formData.get('pUnit'),
      bagWeight: parseFloat(formData.get('pBagWeight')) || 0
    };

    if (editingProduct) {
      newProduct.id = editingProduct.id;
      const res = await api.updateProduct(newProduct);
      if (!res.error) {
        alert(res.message || "Product updated");
        setEditingProduct(null);
        fetchData();
        e.target.reset();
      } else {
        alert("Error: " + res.message);
      }
    } else {
      const res = await api.addProduct(newProduct);
      if (!res.error) {
        alert(res.message || "Product added");
        fetchData();
        e.target.reset();
      } else {
        alert("Error: " + res.message);
      }
    }
  };

  const editInventory = (product) => {
    setEditingProduct(product);
  };

  const deleteFromInventory = async (id) => {
    if (window.confirm(t[lang].confirmDelete)) {
      if (cart.find(item => item.id === id)) {
        alert(lang === 'ur' ? "پہلے اس آئٹم کو بل سے ختم کریں" : "Remove item from bill first");
        return;
      }
      await api.deleteProduct(id);
      fetchData();
    }
  };

  // --- 4. BILLING ENTRY STATE ---
  const [entry, setEntry] = useState(null); // { ...product, qty, bags, mode: 'add'|'edit', cartIndex }

  const handleSelectProduct = (product) => {
    // Default: 1 bag if bagWeight > 0, else 1 unit
    let initialQty = 1;
    let initialBags = 0;

    if (product.unit === 'kg' && product.bagWeight > 0) {
      initialQty = 0; // Default to 0 per user request
      initialBags = 1;
    }

    setEntry({
      ...product,
      qty: initialQty,
      bags: initialBags,
      currentSellPrice: product.sellPrice, // standard sell price
      currentBagPrice: (product.bagWeight > 0) ? (product.sellPrice * product.bagWeight) : 0,
      mode: 'add'
    });
    setSearchTerm(""); // Hide search list
  };

  const handleEditCartItem = (item, index) => {
    setEntry({
      ...item,
      // If editing a Bag item, set Qty to 0 for the UI
      qty: (item.bags > 0) ? 0 : item.qty,
      currentSellPrice: item.sellPrice,
      currentBagPrice: (item.bagWeight > 0) ? (item.sellPrice * item.bagWeight) : 0,
      mode: 'edit',
      cartIndex: index
    });
  };

  const handleEntryChange = (field, value) => {
    if (!entry) return;
    const newEntry = { ...entry };

    // Allow clearing input (empty string)
    if (value === '') {
      if (field === 'qty') {
        newEntry.qty = '';
        // Don't touch bags
      }
      else if (field === 'bags') {
        newEntry.bags = '';
        // Don't touch qty
      }
      else if (field === 'price') {
        newEntry.currentSellPrice = '';
        newEntry.currentBagPrice = 0; // Sync prices still useful? Maybe keep price sync
      }
      else if (field === 'bagPrice') {
        newEntry.currentBagPrice = '';
        newEntry.currentSellPrice = 0;
      }
      setEntry(newEntry);
      return;
    }

    const val = parseFloat(value);

    if (field === 'qty') {
      if (!isNaN(val)) {
        newEntry.qty = val;
        // User Logic: "if user send quantity then bag should be zero"
        newEntry.bags = 0;
      }
    } else if (field === 'bags') {
      if (!isNaN(val)) {
        newEntry.bags = val;
        // User Logic: "If user add bag then send quantity to 0"
        newEntry.qty = 0;
      }
    } else if (field === 'price') {
      newEntry.currentSellPrice = val || 0;
      if (newEntry.bagWeight > 0) {
        newEntry.currentBagPrice = (val || 0) * newEntry.bagWeight;
      }
    } else if (field === 'bagPrice') {
      newEntry.currentBagPrice = val || 0;
      if (newEntry.bagWeight > 0) {
        newEntry.currentSellPrice = (val || 0) / newEntry.bagWeight;
      }
    }

    setEntry(newEntry);
  };

  const handleInputFocus = (e, field) => {
    // If the value is practically 0, clear it
    if (parseFloat(e.target.value) === 0) {
      handleEntryChange(field, '');
    } else {
      e.target.select();
    }
  };

  const commitToCart = (e) => {
    e.preventDefault();
    if (!entry) return;

    // SAFETY: Ensure we work with Numbers
    const entryBags = Number(entry.bags) || 0;
    let entryQty = Number(entry.qty) || 0;
    const entryPrice = Number(entry.currentSellPrice) || 0;

    // Derived Qty Logic: If Bags entered, calculate Qty
    if (entryBags > 0 && entry.bagWeight > 0) {
      entryQty = entryBags * entry.bagWeight;
    }

    if (entryQty <= 0) {
      setMessage("Quantity / Bags must be greater than 0");
      setTimeout(() => setMessage(""), 2000);
      return;
    }

    // Check Stock
    let effectiveStock = Number(entry.remainingStock) || 0;
    if (entry.mode === 'edit') {
      const oldItem = cart[entry.cartIndex];
      if (oldItem) effectiveStock += oldItem.qty;
    }

    if (effectiveStock < entryQty) {
      // Just warn
      setMessage(`${t[lang].outOfStock} (${entry.name})`);
      setTimeout(() => setMessage(""), 3000);
    }

    if (entry.mode === 'edit') {
      // EDIT MODE: REPLACE Logic
      const oldItem = cart[entry.cartIndex];
      const stockDiff = entryQty - oldItem.qty; // New - Old

      const updatedItem = {
        ...oldItem,
        qty: entryQty,
        bags: entryBags,
        sellPrice: entryPrice,
        bagWeight: entry.bagWeight
      };

      const newCart = [...cart];
      newCart[entry.cartIndex] = updatedItem;
      setCart(newCart);

      // Update Stock: Subtract the difference
      setInventory(inventory.map(p => p.id === entry.id ? { ...p, remainingStock: (Number(p.remainingStock) || 0) - stockDiff } : p));

    } else {
      // ADD MODE: MERGE Logic
      const existingIndex = cart.findIndex(item => item.id === entry.id);
      if (existingIndex >= 0) {
        const existing = cart[existingIndex];
        const newQty = existing.qty + entryQty;
        const newBags = (existing.bags || 0) + entryBags;

        const updatedItem = {
          ...existing,
          qty: newQty,
          bags: newBags,
          sellPrice: entryPrice,
          bagWeight: entry.bagWeight
        };

        const newCart = [...cart];
        newCart[existingIndex] = updatedItem;
        setCart(newCart);

        // Stock update
        setInventory(inventory.map(p => p.id === entry.id ? { ...p, remainingStock: (Number(p.remainingStock) || 0) - entryQty } : p));

      } else {
        // Add new
        const newItem = {
          ...entry,
          qty: entryQty,
          bags: entryBags,
          sellPrice: entryPrice,
          bagWeight: entry.bagWeight
        };
        setCart([...cart, newItem]);
        // Stock update
        setInventory(inventory.map(p => p.id === entry.id ? { ...p, remainingStock: (Number(p.remainingStock) || 0) - entryQty } : p));
      }
    }

    setEntry(null); // Clear entry form
    document.querySelector('.search-bar')?.focus();
  };

  const removeFromCart = (itemToRemove) => {
    // Restore stock in UI
    setInventory(inventory.map(p => p.id === itemToRemove.id ? { ...p, remainingStock: (Number(p.remainingStock) || 0) + itemToRemove.qty } : p));
    setCart(cart.filter(item => item !== itemToRemove));
  };

  const calculateTotal = () => cart.reduce((acc, item) => acc + (Number(item.sellPrice) * item.qty), 0);
  // const changeAmount = receivedAmount - calculateTotal(); // Unused

  const filteredInventory = inventory.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()));

  const resetBill = () => {
    setCart([]);
    setReceivedAmount(0);
    setEditingOrderId(null);
    setSelectedCustomer(null);
    fetchData();
  };

  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const handleCheckout = async (avoidPrint = false) => {
    if (cart.length === 0) return Promise.resolve();
    const total = calculateTotal();

    // Prepare order object
    const orderData = {
      items: cart,
      total: total,
      received: total,
      change: 0,
      customerId: selectedCustomer ? selectedCustomer.id : null
    };

    let res;
    if (editingOrderId) {
      orderData.id = editingOrderId;
      res = await api.updateOrder(orderData);
    } else {
      res = await api.addOrder(orderData);
    }

    if (!res.error) {
      if (!avoidPrint) {
        alert(editingOrderId ? "Order Updated!" : "Order Saved!");
      }

      // Print receipt via BillingComponent (Thermal or Fallback)
      if (!avoidPrint) {
        if (billingRef.current) {
          setTimeout(() => {
            billingRef.current.handleThermalPrint(true); // true = reset bill after print
          }, 100);
        } else {
          // Fallback if ref not ready
          resetBill();
        }
      }

      return Promise.resolve(res);
    } else {
      alert("Failed to save order: " + res.message);
      return Promise.reject(new Error(res.message));
    }
  };

  // --- ORDER MANAGEMENT ---
  const handleDeleteOrder = async (id) => {
    if (window.confirm(t[lang].confirmDelete)) {
      const res = await api.deleteOrder(id);
      if (!res.error) {
        fetchData();
      } else {
        alert(res.message);
      }
    }
  };

  const handleEditOrder = async (orderId) => {
    const details = await api.getOrderDetails(orderId);
    if (Array.isArray(details)) {
      // Flatten items to match cart structure with BAG logic
      const loadedCart = details.map(d => {
        const qty = Number(d.qty);
        const bagWeight = Number(d.bagWeight);
        const sellPrice = Number(d.sellPrice);
        return {
          ...d,
          qty,
          bagWeight,
          sellPrice,
          bags: (bagWeight > 0 && qty % bagWeight === 0) ? (qty / bagWeight) : 0
        };
      });

      setCart(loadedCart);
      setEditingOrderId(orderId);

      // Find original order metadata to set customer and received amount
      const originalOrder = orders.find(o => o.id === orderId);
      if (originalOrder) {
        setReceivedAmount(Number(originalOrder.received));
        if (originalOrder.customerId) {
          const cust = customers.find(c => c.id === originalOrder.customerId);
          setSelectedCustomer(cust || null);
        } else {
          setSelectedCustomer(null);
        }
      }
      setView('pos');
    }
  };

  const printOrder = async (orderId) => {
    const details = await api.getOrderDetails(orderId);
    const originalOrder = orders.find(o => o.id === orderId);

    if (Array.isArray(details) && originalOrder) {
      const loadedCart = details.map(d => {
        const qty = Number(d.qty);
        const bagWeight = Number(d.bagWeight);
        const sellPrice = Number(d.sellPrice);
        return {
          ...d,
          qty,
          bagWeight,
          sellPrice,
          bags: (bagWeight > 0 && qty % bagWeight === 0) ? (qty / bagWeight) : 0
        };
      });

      // Load data into POS view
      setCart(loadedCart);
      if (originalOrder.customerId) {
        const cust = customers.find(c => c.id === originalOrder.customerId);
        setSelectedCustomer(cust || null);
      } else {
        setSelectedCustomer(null);
      }
      setReceivedAmount(Number(originalOrder.received));
      setEditingOrderId(null); // Just printing, not editing (unless user clicks edit later)

      setView('pos');

      // Schedule print via BillingComponent
      setTimeout(() => {
        if (billingRef.current) {
          billingRef.current.handleThermalPrint(false); // false = DO NOT reset bill
        }
      }, 500);
    }
  };


  return (
    <div className={`app-container ${lang === 'ur' ? 'rtl-mode' : ''}`}>
      <nav className="no-print">
        <h1>{t[lang].storeName}</h1>
        <div className="nav-controls">
          <div className="nav-buttons">
            <button className={view === 'inventory' ? 'active' : ''} onClick={() => setView('inventory')}>{t[lang].inventory}</button>
            <button className={view === 'pos' ? 'active' : ''} onClick={() => { resetBill(); setView('pos'); }}>{t[lang].billing}</button>
            <button className={view === 'customers' ? 'active' : ''} onClick={() => setView('customers')}>{t[lang].customers}</button>
            <button className={view === 'orders' ? 'active' : ''} onClick={() => { fetchData(); setView('orders'); }}>{t[lang].orders}</button>
          </div>
          <select className="lang-dropdown" value={lang} onChange={(e) => setLang(e.target.value)}>
            <option value="ur">اردو (Urdu)</option>
            <option value="en">English</option>
          </select>
        </div>
      </nav>

      <main className="content-area">
        {message && <div className="status-message no-print">{message}</div>}

        {/* CUSTOMER VIEW */}
        {view === 'customers' && (
          <div className="panel no-print">
            <h3>{t[lang].addCustomer}</h3>
            <form onSubmit={handleAddCustomer} className="entry-form">
              <input name="cName" placeholder={t[lang].name} required />
              <input name="cPhone" placeholder={t[lang].phone} required />
              <input name="cAddress" placeholder={t[lang].address} required />
              <button type="submit">{t[lang].save}</button>
            </form>

            <div className="search-container">
              <input
                type="text"
                className="search-bar"
                placeholder={t[lang].search}
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
              />
            </div>

            <table className="inventory-table">
              <thead>
                <tr>
                  <th>{t[lang].name}</th>
                  <th>{t[lang].phone}</th>
                  <th>{t[lang].address}</th>
                  <th>{t[lang].actions}</th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map(c => (
                  <tr key={c.id}>
                    <td>{c.name}</td>
                    <td>{c.phone}</td>
                    <td>{c.address}</td>
                    <td>
                      <button className="delete-btn" onClick={() => deleteCustomer(c.id)}>🗑️</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* INVENTORY VIEW */}
        {view === 'inventory' && (
          <div className="panel no-print">
            <h3>{editingProduct ? t[lang].editAction : t[lang].addItem}</h3>
            <form key={editingProduct ? editingProduct.id : 'new'} onSubmit={handleAddProduct} className="entry-form">
              <input name="pName" defaultValue={editingProduct?.name} placeholder={t[lang].name} required />
              <input name="pBuyPrice" defaultValue={editingProduct?.buyPrice} type="number" step="0.01" placeholder={t[lang].buyPrice} required />
              <input name="pSellPrice" defaultValue={editingProduct?.sellPrice} type="number" step="0.01" placeholder={t[lang].sellPrice} required />
              <input name="pStock" defaultValue={editingProduct?.remainingStock} type="number" step="0.01" placeholder={t[lang].stock} required />
              <input name="pBagWeight" defaultValue={editingProduct?.bagWeight} type="number" step="0.01" placeholder={t[lang].bagWeight} />
              <select name="pUnit" defaultValue={editingProduct?.unit || 'unit'} className="unit-dropdown">
                <option value="unit">{t[lang].piece}</option>
                <option value="kg">{t[lang].kg}</option>
              </select>
              <button type="submit">{t[lang].save}</button>
              {editingProduct && (
                <button type="button" onClick={() => setEditingProduct(null)} style={{ marginLeft: '10px' }}>
                  Cancel
                </button>
              )}
            </form>

            <div className="search-container" style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
              <input
                type="text"
                className="search-bar"
                placeholder={t[lang].search}
                value={inventorySearchInput}
                onChange={(e) => setInventorySearchInput(e.target.value)}
              />
              <button
                onClick={() => setInventorySearchTerm(inventorySearchInput)}
                style={{ padding: '8px 15px', background: '#2196f3', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                {t[lang].search || 'Search'}
              </button>
            </div>

            <table className="inventory-table">
              <thead>
                <tr>
                  <th>{t[lang].name}</th>
                  <th>{t[lang].sellPrice}</th>
                  <th>{t[lang].profit}</th>
                  <th>{t[lang].remaining}</th>
                  <th>{t[lang].actions}</th>
                </tr>
              </thead>
              <tbody>
                {inventory.filter(p => p.name.toLowerCase().includes(inventorySearchTerm.toLowerCase())).map(p => (
                  <tr key={p.id}>
                    <td>{p.name}</td>
                    <td>{p.sellPrice}</td>
                    <td style={{ color: (p.sellPrice - p.buyPrice) >= 0 ? 'green' : 'red' }}>
                      {(p.sellPrice - p.buyPrice).toFixed(2)}
                    </td>
                    <td>{formatQty(p.remainingStock)} {p.unit === 'kg' ? t[lang].kg : t[lang].piece}</td>
                    <td>
                      <button className="action-btn" onClick={() => editInventory(p)}>✏️</button>
                      <button className="delete-btn" onClick={() => deleteFromInventory(p.id)}>🗑️</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ORDERS VIEW */}
        {view === 'orders' && (
          <div className="panel no-print">
            <h3>{t[lang].orders}</h3>

            {/* DASHBOARD CARDS */}
            <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, background: '#e3f2fd', padding: '15px', borderRadius: '8px', borderLeft: '5px solid #2196f3', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#1565c0' }}>{t[lang].totalOrders}</h4>
                <p style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>{orders.length}</p>
              </div>
              <div style={{ flex: 1, background: '#e8f5e9', padding: '15px', borderRadius: '8px', borderLeft: '5px solid #4caf50', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#2e7d32' }}>{t[lang].totalRevenue}</h4>
                <p style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
                  Rs. {orders.reduce((acc, o) => acc + (Number(o.total) || 0), 0).toFixed(2)}
                </p>
              </div>
              <div style={{ flex: 1, background: '#fff3e0', padding: '15px', borderRadius: '8px', borderLeft: '5px solid #ff9800', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#ef6c00' }}>{t[lang].profitLoss}</h4>
                <p style={{ fontSize: '24px', fontWeight: 'bold', margin: 0, color: orders.reduce((acc, o) => acc + (Number(o.profit) || 0), 0) >= 0 ? '#2e7d32' : '#c62828' }}>
                  Rs. {orders.reduce((acc, o) => acc + (Number(o.profit) || 0), 0).toFixed(2)}
                </p>
              </div>
            </div>

            <table className="inventory-table">
              <thead>
                <tr>
                  <th>{t[lang].orderId}</th>
                  <th>{t[lang].customer}</th>
                  <th>{t[lang].date}</th>
                  <th>{t[lang].total}</th>
                  <th>{t[lang].actions}</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.id}>
                    <td>{o.id}</td>
                    <td>{o.customerName || '-'}</td>
                    <td>{new Date(o.date).toLocaleString()}</td>
                    <td>{Number(o.total).toFixed(2)}</td>
                    <td>
                      <button className="action-btn" onClick={() => printOrder(o.id)}>🖨️</button>
                      <button className="action-btn" onClick={() => handleEditOrder(o.id)}>✏️</button>
                      <button className="delete-btn" onClick={() => handleDeleteOrder(o.id)}>🗑️</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* BILLING VIEW (POS) */}
        {view === 'pos' && (
          <div className="pos-container">
            <div className="selection-area no-print">
              <h3>{t[lang].selectItems}</h3>
              <input
                type="text"
                className="search-bar"
                placeholder={t[lang].search}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="product-list">
                {/* Only Show if Search Term is Present */}
                {searchTerm && filteredInventory.map(p => (
                  <div key={p.id} className="item-card" onClick={() => handleSelectProduct(p)}>
                    <strong>{p.name}</strong>
                    <p>Rs. {p.sellPrice}</p>
                    <small>{formatQty(p.remainingStock)} {p.unit === 'kg' ? t[lang].kg : t[lang].piece}</small>
                  </div>
                ))}
              </div>

              {/* NEW ENTRY FORM */}
              {entry && (
                <div className="entry-panel" style={{ border: '2px solid #28a745', padding: '10px', marginTop: '10px', background: '#e8f5e9' }}>
                  <h4>{entry.name}</h4>
                  <form onSubmit={commitToCart} style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>

                    {/* 1. QTY */}
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8em' }}>{t[lang].qty}</label>
                      <input
                        type="number"
                        step="0.01"
                        value={entry.qty}
                        onChange={(e) => handleEntryChange('qty', e.target.value)}
                        onFocus={(e) => handleInputFocus(e, 'qty')}
                        style={{ width: '80px', padding: '5px' }}
                      />
                      {entry.unit === 'kg' && <span style={{ fontSize: '0.8em' }}>kg</span>}
                    </div>

                    {/* 2. BAGS (Conditional) */}
                    {entry.bagWeight > 0 && (
                      <div>
                        <label style={{ display: 'block', fontSize: '0.8em' }}>{t[lang].bags}</label>
                        <input
                          type="number"
                          step="0.1"
                          value={entry.bags}
                          onChange={(e) => handleEntryChange('bags', e.target.value)}
                          onFocus={(e) => handleInputFocus(e, 'bags')}
                          style={{ width: '60px', padding: '5px' }}
                        />
                      </div>
                    )}

                    {/* 3. SELL PRICE (UNIT RATE) */}
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8em' }}>{t[lang].sellPrice}</label>
                      <input
                        type="number"
                        step="0.01"
                        value={entry.currentSellPrice}
                        onChange={(e) => handleEntryChange('price', e.target.value)}
                        onFocus={(e) => handleInputFocus(e, 'price')}
                        style={{ width: '80px', padding: '5px' }}
                      />
                    </div>

                    {/* 4. BAG PRICE (Conditional) */}
                    {entry.bagWeight > 0 && (
                      <div>
                        <label style={{ display: 'block', fontSize: '0.8em' }}>{t[lang].bagPrice}</label>
                        <input
                          type="number"
                          step="0.01"
                          value={entry.currentBagPrice}
                          onChange={(e) => handleEntryChange('bagPrice', e.target.value)}
                          onFocus={(e) => handleInputFocus(e, 'bagPrice')}
                          style={{ width: '80px', padding: '5px' }}
                        />
                      </div>
                    )}

                    {/* 5. TOTAL */}
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8em' }}>{t[lang].total}</label>
                      <input
                        type="number"
                        value={(entry.currentSellPrice * entry.qty).toFixed(2)}
                        readOnly
                        disabled
                        style={{ width: '100px', padding: '5px', background: '#e9ecef', fontWeight: 'bold' }}
                      />
                    </div>

                    <button type="submit" style={{ background: '#28a745', color: 'white', padding: '8px 15px', border: 'none', cursor: 'pointer' }}>
                      {entry.mode === 'edit' ? t[lang].save : 'Add'}
                    </button>
                    <button type="button" onClick={() => setEntry(null)} style={{ background: '#dc3545', color: 'white', padding: '8px 15px', border: 'none', cursor: 'pointer' }}>
                      Cancel
                    </button>
                  </form>
                </div>
              )}
            </div>

            <div className="receipt-area">
              <div id="receipt" className="receipt-paper">
                <div className="receipt-header">
                  <h2>{t[lang].storeName}</h2>
                  <p>{t[lang].Ariwala}</p>
                  <div className="receipt-date">
                    <span>{t[lang].date}: {new Date().toLocaleDateString()}</span>
                    <span>{t[lang].time}: {new Date().toLocaleTimeString()}</span>
                  </div>
                </div>
                <hr />
                <table className="bill-table">
                  <thead>
                    <tr>
                      <th>{t[lang].item}</th>
                      <th>{t[lang].sellPrice}</th>
                      <th>{t[lang].bags}</th> {/* NEW BAG COl */}
                      <th>{t[lang].qty}</th>
                      <th>{t[lang].total}</th>
                      <th className="no-print"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {cart.map((item, i) => {
                      const bw = Number(item.bagWeight);
                      const bwFinal = (Number.isFinite(bw) && bw > 0) ? bw : 0;
                      let dBags = 0;
                      let dQty = item.qty;
                      if (bwFinal > 0) {
                        dBags = Math.floor(item.qty / bwFinal);
                        dQty = item.qty % bwFinal;
                      }
                      return (
                        <tr key={i} onClick={() => handleEditCartItem(item, i)} style={{ cursor: 'pointer', background: (entry && entry.cartIndex === i) ? '#fff3cd' : 'transparent' }}>
                          {/* READ ONLY CELLS */}
                          <td>{item.name}</td>
                          <td>{Number(item.sellPrice).toFixed(2)}</td>
                          {/* BAGS INPUT */}
                          <td>{dBags > 0 ? dBags : '-'}</td>
                          <td>{dQty} {item.unit === 'kg' ? t[lang].kg : ''}</td>
                          <td>{(Number(item.sellPrice) * item.qty).toFixed(2)}</td>
                          <td className="no-print" onClick={(e) => e.stopPropagation()}>
                            <button className="remove-btn" onClick={() => removeFromCart(item)}>×</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <hr />
                <div className="bill-summary">
                  <div className="summary-row">
                    <strong>{t[lang].total}:</strong>
                    <strong>{calculateTotal().toFixed(2)}</strong>
                  </div>

                  <div className="summary-row no-print" style={{ marginTop: '10px' }}>
                    <select value={selectedCustomer ? selectedCustomer.id : ""} onChange={(e) => {
                      const c = customers.find(cu => cu.id === Number(e.target.value));
                      setSelectedCustomer(c || null);
                    }}>
                      <option value="">{t[lang].customerSelect}</option>
                      {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  {selectedCustomer && <div className="summary-row"><small>{t[lang].name}: {selectedCustomer.name}</small></div>}
                </div>
                <p className="footer-msg">{t[lang].thanks}</p>

                {/* --- PRINT FOOTER DETAILS --- */}
                <div className="receipt-footer">
                  <hr />
                  <p className="dev-tag">{t[lang].devBy}</p>
                  <p className="dev-contact">03299829425 | 03067857439</p>
                </div>
              </div>


              <div className="no-print action-btns">
                <BillingComponent
                  ref={billingRef}
                  cartItems={cart}
                  orderMeta={{
                    customerName: selectedCustomer ? selectedCustomer.name : '',
                    cashier: "Admin"
                  }}
                  total={calculateTotal()}
                  translations={t[lang]}
                  isRtl={lang === 'ur'}
                  selectedCustomer={selectedCustomer}
                  resetBill={resetBill}
                  onPrint={() => handleCheckout(true)}
                />
                <button className="print-btn" style={{ backgroundColor: editingOrderId ? '#007bff' : '#28a745' }} onClick={handleCheckout}>
                  {editingOrderId ? t[lang].updateOrder : t[lang].checkout}
                </button>
                <button className="clear-btn" onClick={resetBill}>{t[lang].newBill}</button>
              </div>
            </div>
          </div>
        )
        }
      </main >


      <footer className="app-footer no-print">
        <div className="footer-content">
          <p>© 2026 <strong>{t[lang].storeName}</strong></p>
          <div className="dev-branding">
            <span>{t[lang].devBy}</span>
            <span className="separator">|</span>
            <span>📞  0329 9829425| 📞 0306 7857439</span>
          </div>
        </div>
      </footer>
    </div >
  );
}

export default App;